import type { NewDashboardOrgType } from 'helpers/repTypeHelper';

const publicSvg = (filename: string) =>
  `${process.env.PUBLIC_URL ?? ''}/${encodeURI(filename)}`;

const clampRankValue = (value: number | null | undefined): number | null => {
  if (value == null || Number.isNaN(value)) return null;
  return Math.max(0, Math.min(5, Math.round(value)));
};

const RANK_MAP = {
  'LEVEL 1': 1,
  'LEVEL 2': 2,
  'LEVEL 3': 3,
  'LEVEL 4': 4,
  PREMIER: 4,
  FRANCHISE: 5,
};

const DEFAULT_RANK_ICON_SIZE_PX = 56;

type RankIconStyleProps = {
  sizePx?: number;
  opacity?: number;
};

type GetRankIconForValueOptions = {
  rank: number | null | undefined;
  sizePx?: number;
  opacity?: number;
  orgType?: NewDashboardOrgType;
};

type RankLevelIconComponent = (props: RankIconStyleProps) => JSX.Element;

const getRankLevelIconComponent = (
  level: number,
  orgType?: NewDashboardOrgType,
): RankLevelIconComponent | null => {
  if ((orgType === 'BLOCK' || orgType === 'CHAIN') && level === 4) {
    return RankLevelFusion4;
  }

  switch (level) {
    case 1:
      return RankLevel1;
    case 2:
      return RankLevel2;
    case 3:
      return RankLevel3;
    case 4:
      return RankLevelPremier;
    case 5:
      return RankLevelFranchise;
    default:
      return null;
  }
};

const getIconForRankValue = ({
  rank,
  sizePx = DEFAULT_RANK_ICON_SIZE_PX,
  opacity = 1,
  orgType,
}: GetRankIconForValueOptions) => {
  const r = clampRankValue(rank);
  if (r == null) {
    return null;
  }

  const iconProps = { sizePx, opacity };
  if (r === 0) {
    return <RankLevel0 {...iconProps} />;
  }

  const Icon = getRankLevelIconComponent(r, orgType);
  return Icon ? <Icon {...iconProps} /> : null;
};

const RankLevel0 = (props: RankIconStyleProps) => (
  <img
    src={publicSvg('Street Status 0.svg')}
    alt="Rank level 0"
    style={{
      width: props.sizePx,
      height: props.sizePx,
      opacity: props.opacity,
    }}
  />
);

const RankLevel1 = (props: RankIconStyleProps) => (
  <img
    src={publicSvg('Street Status 1.svg')}
    alt="Rank level 1"
    style={{
      width: props.sizePx,
      height: props.sizePx,
      opacity: props.opacity,
    }}
  />
);

const RankLevel2 = (props: RankIconStyleProps) => (
  <img
    src={publicSvg('Street Status 2.svg')}
    alt="Rank level 2"
    style={{
      width: props.sizePx,
      height: props.sizePx,
      opacity: props.opacity,
    }}
  />
);

const RankLevel3 = (props: RankIconStyleProps) => (
  <img
    src={publicSvg('Street Status 3.svg')}
    alt="Rank level 3"
    style={{
      width: props.sizePx,
      height: props.sizePx,
      opacity: props.opacity,
    }}
  />
);

const RankLevelPremier = (props: RankIconStyleProps) => (
  <img
    src={publicSvg('Street Status Premier.svg')}
    alt="Rank level premier"
    style={{
      width: props.sizePx,
      height: props.sizePx,
      opacity: props.opacity,
    }}
  />
);

const RankLevelFusion4 = (props: RankIconStyleProps) => (
  <img
    src={publicSvg('Fusion Level 4.svg')}
    alt="Rank level 4"
    style={{
      width: props.sizePx,
      height: props.sizePx,
      opacity: props.opacity,
    }}
  />
);

const RankLevelFranchise = (props: RankIconStyleProps) => (
  <img
    src={publicSvg('Franchise Icon.svg')}
    alt="Rank level franchise"
    style={{
      width: props.sizePx,
      height: props.sizePx,
      opacity: props.opacity,
    }}
  />
);

export {
  getIconForRankValue,
  clampRankValue,
  RankLevel0,
  RankLevel1,
  RankLevel2,
  RankLevel3,
  RankLevelPremier,
  RankLevelFranchise,
  RANK_MAP,
};
